import React, { useState } from "react";

import { Edit, Plus, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { toast } from "~/hooks/use-toast";
import { AuxiliaryType, useAuxiliaryTypes } from "~/hooks/useAuxiliaryTypes";

interface AuxiliaryTypeManagementModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const AuxiliaryTypeManagementModal: React.FC<
  AuxiliaryTypeManagementModalProps
> = ({ isOpen, onOpenChange }) => {
  const {
    auxiliaryTypes,
    isLoading,
    createAuxiliaryType,
    updateAuxiliaryType,
    deleteAuxiliaryType,
    isCreating,
    isUpdating,
    isDeleting,
  } = useAuxiliaryTypes();

  const [newTypeName, setNewTypeName] = useState("");
  const [editingType, setEditingType] = useState<AuxiliaryType | null>(null);
  const [editName, setEditName] = useState("");

  const handleCreate = () => {
    if (!newTypeName.trim()) {
      toast({
        title: "Error",
        description: "El nombre del tipo de auxiliar es requerido.",
        variant: "destructive",
      });
      return;
    }

    createAuxiliaryType(newTypeName.trim());
    setNewTypeName("");
  };

  const handleEdit = (type: AuxiliaryType) => {
    setEditingType(type);
    setEditName(type.name);
  };

  const handleUpdate = () => {
    if (!editingType || !editName.trim()) {
      toast({
        title: "Error",
        description: "El nombre del tipo de auxiliar es requerido.",
        variant: "destructive",
      });
      return;
    }

    updateAuxiliaryType({ id: editingType.id, name: editName.trim() });
    setEditingType(null);
    setEditName("");
  };

  const handleDelete = (type: AuxiliaryType) => {
    if (
      window.confirm(
        `¿Está seguro de que desea eliminar el tipo "${type.name}"? Esta acción no se puede deshacer.`
      )
    ) {
      deleteAuxiliaryType(type.id);
    }
  };

  const cancelEdit = () => {
    setEditingType(null);
    setEditName("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Gestionar Tipos de Auxiliar</DialogTitle>
        </DialogHeader>

        {/* Create New Type */}
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="new-type">Nuevo Tipo de Auxiliar</Label>
              <Input
                id="new-type"
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="Nombre del tipo"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleCreate();
                  }
                }}
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleCreate}
                disabled={isCreating || !newTypeName.trim()}
                size="sm"
              >
                <Plus className="mr-1 h-4 w-4" />
                Crear
              </Button>
            </div>
          </div>

          {/* Types Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center">
                      Cargando...
                    </TableCell>
                  </TableRow>
                ) : auxiliaryTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center">
                      No hay tipos de auxiliar
                    </TableCell>
                  </TableRow>
                ) : (
                  auxiliaryTypes.map((type, index) => (
                    <TableRow
                      key={type.id}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                    >
                      <TableCell>
                        {editingType?.id === type.id ? (
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                handleUpdate();
                              } else if (e.key === "Escape") {
                                cancelEdit();
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          type.name
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {editingType?.id === type.id ? (
                          <div className="space-x-2">
                            <Button
                              onClick={handleUpdate}
                              disabled={isUpdating}
                              size="sm"
                              variant="outline"
                            >
                              Guardar
                            </Button>
                            <Button
                              onClick={cancelEdit}
                              size="sm"
                              variant="ghost"
                            >
                              Cancelar
                            </Button>
                          </div>
                        ) : (
                          <div className="space-x-2">
                            <Button
                              onClick={() => handleEdit(type)}
                              size="sm"
                              variant="ghost"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => handleDelete(type)}
                              disabled={isDeleting}
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuxiliaryTypeManagementModal;
